import { Hazard } from 'features/hazards/hazard.types';
import { API } from 'api';
import { AxiosResponse } from 'axios';
import {
  normalizeLinkValues,
  parseFormLinks,
} from 'features/geoEvents/geoEvent.utils';
import { NotificationType } from 'features/notifications/notifications.types';
import { getReportMedia } from 'components/MediaForm';
import { HazardFormValues, HazardFormServerValues } from './HazardForm.types';
import { GeoEventTypes, HazardTypes } from '../../../../constants';

export const postHazard = ({
  data,
  skipRedundancyCheck,
}: {
  data: HazardFormServerValues;
  skipRedundancyCheck?: boolean;
}): Promise<AxiosResponse<Hazard>> =>
  API.post(`geo_events/`, {
    ...data,
    ...(skipRedundancyCheck && { skipRedundancyCheck }),
  });

export const updateHazard = (data: {
  id: number;
  data: HazardFormServerValues;
}): Promise<AxiosResponse<Hazard>> =>
  API.put(`geo_events/${data.id}`, data.data);

export const getInitialValues = (input: {
  hazard?: Hazard;
  latitude?: number;
  longitude?: number;
  initialAlertCameraId?: string | undefined;
}): HazardFormValues => {
  const { hazard, latitude, longitude, initialAlertCameraId } = input;
  return {
    name: hazard?.name || '',
    address: hazard?.address || '',
    type: hazard?.data.hazardType || HazardTypes.other,
    parentGeoEvents: hazard?.parentGeoEvents || [],
    // eslint-disable-next-line no-nested-ternary
    status: hazard ? (hazard.isActive ? 'active' : 'inactive') : 'active',
    expandLinks: Boolean(hazard?.data?.links.length),
    links: parseFormLinks(hazard?.data.links || []),
    lat: hazard?.lat || latitude || 0,
    lng: hazard?.lng || longitude || 0,
    regions: hazard?.regions ?? [],
    // Report fields (only used on create)
    reportNotificationType: NotificationType.Silent,
    reportMessage: '',
    // If we are creating a hazard from a camera, initialize the initial report media picker
    // into alert camera mode so the existing `MediaAlertCameraForm` can apply prefill.
    asset: initialAlertCameraId ? 'alertCamera' : null,
    media: null,
    fileType: '',
    embedUrl: '',
    mediaLat: '',
    mediaLng: '',
    mediaAz: 0,
  };
};

export const hazardFormDataToServerData = (
  values: HazardFormValues,
  mediaUrl?: string,
): HazardFormServerValues | null => {
  const { status, type, parentGeoEvents } = values;
  if (!type) return null;

  const hazardData: HazardFormServerValues = {
    name: values.name,
    address: values.address,
    lat: values.lat,
    lng: values.lng,
    regions: values.regions,
    isActive: status === 'active',
    geoEventType: GeoEventTypes.hazard,
    parentGeoEvents: parentGeoEvents.map((parentGeoEvent) => ({
      id: parentGeoEvent.id,
    })),
    data: {
      geoEventType: GeoEventTypes.hazard,
      hazardType: type,
      links: normalizeLinkValues(values.links),
    },
  };

  // Build initialReport if any report data is provided
  if (values.reportMessage || values.embedUrl || values.media) {
    hazardData.initialReport = {};
  }
  if (values.reportMessage) {
    hazardData.initialReport!.message = values.reportMessage;
  }
  if (values.embedUrl) {
    hazardData.initialReport!.embedUrl = values.embedUrl;
  }
  if (values.media) {
    hazardData.initialReport!.media = getReportMedia({
      media: values.media,
      fileType: values.fileType,
      lat: values.mediaLat,
      lng: values.mediaLng,
      az: values.mediaAz,
      mediaUrl,
    });
  }

  return hazardData;
};
